import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  Container,
  Typography,
  Box,
  Card,
  CardActionArea,
  Chip,
} from '@mui/material';
import {
  People,
  Chat,
  Report,
  CardGiftcard,
  AdminPanelSettings,
  TrendingUp,
  Schedule,
  FavoriteRounded,
  RateReview,
  HelpOutline,
  Gavel,
  QuestionAnswer,
} from '@mui/icons-material';

// ─── Design tokens ────────────────────────────────────────────────
const PINK   = '#EC4899';
const CORAL  = '#FF6B35';
const GOLD   = '#FFC107';

// ─── Menu card definitions ────────────────────────────────────────
const menuItems = [
  {
    title: '사용자 관리',
    description: '회원 심사·상태·프로필 관리',
    sub: '등록 심사 및 계정 제어',
    icon: <People sx={{ fontSize: 32 }} />,
    path: '/admin/users',
    gradient: 'linear-gradient(135deg, #1976D2 0%, #42A5F5 100%)',
    accentColor: '#1976D2',
    lightBg: '#E3F2FD',
    stat: '사용자',
    statIcon: <TrendingUp sx={{ fontSize: 14 }} />,
  },
  {
    title: '프로필 심사',
    description: '사진·직업·소개 변경 심사 처리',
    sub: '프로필 변경 심사',
    icon: <RateReview sx={{ fontSize: 32 }} />,
    path: '/admin/profile-reviews',
    gradient: 'linear-gradient(135deg, #7B1FA2 0%, #AB47BC 100%)',
    accentColor: '#7B1FA2',
    lightBg: '#F3E5F5',
    stat: '심사 대기',
    statIcon: <Schedule sx={{ fontSize: 14 }} />,
  },
  {
    title: '큐피트 카드',
    description: '배치 현황·수동 발급·만료 관리',
    sub: '주간 매칭 카드 시스템',
    icon: <CardGiftcard sx={{ fontSize: 32 }} />,
    path: '/admin/cupid-cards',
    gradient: `linear-gradient(135deg, ${PINK} 0%, ${CORAL} 100%)`,
    accentColor: PINK,
    lightBg: '#FDE8F4',
    stat: '이번 주 카드',
    statIcon: <FavoriteRounded sx={{ fontSize: 14 }} />,
  },
  {
    title: '채팅방 관리',
    description: '채팅 모니터링·신고 채팅 처리',
    sub: '실시간 대화 감독',
    icon: <Chat sx={{ fontSize: 32 }} />,
    path: '/admin/chats',
    gradient: 'linear-gradient(135deg, #2E7D32 0%, #66BB6A 100%)',
    accentColor: '#2E7D32',
    lightBg: '#E8F5E9',
    stat: '활성 채팅방',
    statIcon: <Schedule sx={{ fontSize: 14 }} />,
  },
  {
    title: '신고 관리',
    description: '신고 접수·처리·기각 관리',
    sub: '커뮤니티 안전 관리',
    icon: <Report sx={{ fontSize: 32 }} />,
    path: '/admin/reports',
    gradient: 'linear-gradient(135deg, #E65100 0%, #FF9800 100%)',
    accentColor: '#E65100',
    lightBg: '#FFF3E0',
    stat: '대기 신고',
    statIcon: <Report sx={{ fontSize: 14 }} />,
  },
  {
    title: '문의 관리',
    description: '사용자 문의 접수·답변·종결',
    sub: '고객 지원 관리',
    icon: <HelpOutline sx={{ fontSize: 32 }} />,
    path: '/admin/inquiries',
    gradient: 'linear-gradient(135deg, #00695C 0%, #26A69A 100%)',
    accentColor: '#00695C',
    lightBg: '#E0F2F1',
    stat: '미처리 문의',
    statIcon: <HelpOutline sx={{ fontSize: 14 }} />,
  },
  {
    title: '이의제기',
    description: '제재 이의제기 접수·심사·결정',
    sub: '이의제기 심사',
    icon: <Gavel sx={{ fontSize: 32 }} />,
    path: '/admin/appeals',
    gradient: 'linear-gradient(135deg, #4527A0 0%, #7E57C2 100%)',
    accentColor: '#4527A0',
    lightBg: '#EDE7F6',
    stat: '대기 이의제기',
    statIcon: <Gavel sx={{ fontSize: 14 }} />,
  },
  {
    title: 'FAQ 관리',
    description: 'FAQ 등록·수정·고정·비활성화',
    sub: '자주 묻는 질문',
    icon: <QuestionAnswer sx={{ fontSize: 32 }} />,
    path: '/admin/faqs',
    gradient: `linear-gradient(135deg, #AD1457 0%, #EC407A 100%)`,
    accentColor: '#AD1457',
    lightBg: '#FCE4EC',
    stat: '활성 FAQ',
    statIcon: <QuestionAnswer sx={{ fontSize: 14 }} />,
  },
];

// ─── Quick stats (static placeholders) ───────────────────────────
const quickStats = [
  { label: '전체 회원', value: '–', color: '#1976D2', icon: <People sx={{ fontSize: 20 }} /> },
  { label: '심사 대기', value: '–', color: CORAL,     icon: <Schedule sx={{ fontSize: 20 }} /> },
  { label: '이번 주 카드', value: '–', color: PINK,   icon: <FavoriteRounded sx={{ fontSize: 20 }} /> },
  { label: '미처리 신고', value: '–', color: '#E65100', icon: <Report sx={{ fontSize: 20 }} /> },
];

// ─── Component ────────────────────────────────────────────────────
const AdminMainPage: React.FC = () => {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();
  const [timeStr, setTimeStr] = useState('');
  const [dateStr, setDateStr] = useState('');

  // Live clock
  useEffect(() => {
    const update = () => {
      const now = new Date();
      setTimeStr(now.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }));
      setDateStr(now.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' }));
    };
    update();
    const id = setInterval(update, 30_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (isLoading) return;
    if (!user || user.role !== 'ADMIN') navigate('/');
  }, [user, isLoading, navigate]);

  if (isLoading || !user || user.role !== 'ADMIN') return null;

  const adminName = (user as any).name || '관리자';

  return (
    <AdminLayout>
      {/* ── Global keyframes ── */}
      <style>{`
        @keyframes heroFloat {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50%       { transform: translateY(-12px) rotate(3deg); }
        }
        @keyframes heroFloat2 {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50%       { transform: translateY(-8px) rotate(-2deg); }
        }
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0%   { background-position: -200% center; }
          100% { background-position:  200% center; }
        }
        .menu-card {
          transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1),
                      box-shadow 0.3s ease;
        }
        .menu-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 24px 48px rgba(0,0,0,0.15) !important;
        }
        .menu-card:hover .card-icon-ring {
          transform: scale(1.15) rotate(-8deg);
        }
        .card-icon-ring {
          transition: transform 0.35s cubic-bezier(0.34,1.56,0.64,1);
        }
        .stat-pill {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .stat-pill:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.12);
        }
      `}</style>

      {/* ══════════════════════════════════════════════════
          HERO BANNER
      ══════════════════════════════════════════════════ */}
      <Box
        sx={{
          background: `linear-gradient(135deg, #1A0A2E 0%, #2D0A4E 35%, #4A1265 65%, #EC4899 100%)`,
          position: 'relative',
          overflow: 'hidden',
          pt: { xs: 6, md: 8 },
          pb: { xs: 10, md: 12 },
          px: 3,
        }}
      >
        {/* Decorative floating hearts */}
        <Box sx={{
          position: 'absolute', top: 24, right: 80,
          fontSize: 64, opacity: 0.12, color: '#fff',
          animation: 'heroFloat 5s ease-in-out infinite',
          userSelect: 'none', pointerEvents: 'none',
        }}>♥</Box>
        <Box sx={{
          position: 'absolute', top: 60, right: 200,
          fontSize: 32, opacity: 0.08, color: GOLD,
          animation: 'heroFloat2 4s ease-in-out infinite 1s',
          userSelect: 'none', pointerEvents: 'none',
        }}>♦</Box>
        <Box sx={{
          position: 'absolute', bottom: 40, left: 60,
          fontSize: 48, opacity: 0.07, color: '#fff',
          animation: 'heroFloat 6s ease-in-out infinite 0.5s',
          userSelect: 'none', pointerEvents: 'none',
        }}>✦</Box>
        {/* Radial glow */}
        <Box sx={{
          position: 'absolute', top: -80, right: -80,
          width: 400, height: 400, borderRadius: '50%',
          background: `radial-gradient(circle, ${PINK}44 0%, transparent 70%)`,
          pointerEvents: 'none',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Badge */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 40, height: 40, borderRadius: '10px',
              background: `linear-gradient(135deg, ${PINK}, ${CORAL})`,
              boxShadow: `0 4px 16px ${PINK}66`,
            }}>
              <AdminPanelSettings sx={{ fontSize: 22, color: '#fff' }} />
            </Box>
            <Chip
              label="ADMIN CONSOLE"
              size="small"
              sx={{
                background: 'rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.85)',
                border: '1px solid rgba(255,255,255,0.2)',
                fontFamily: 'monospace',
                fontWeight: 700,
                letterSpacing: 2,
                fontSize: '0.65rem',
              }}
            />
          </Box>

          {/* Welcome headline */}
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              color: '#fff',
              lineHeight: 1.15,
              mb: 1.5,
              animation: 'fadeSlideUp 0.6s ease both',
              fontSize: { xs: '1.8rem', md: '2.6rem' },
            }}
          >
            안녕하세요, {adminName}님 👋
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.65)',
              fontSize: '1rem',
              mb: 4,
              animation: 'fadeSlideUp 0.6s ease 0.1s both',
            }}
          >
            Silver Connect 관리 콘솔에 오신 것을 환영합니다
          </Typography>

          {/* Date/time pill */}
          <Box
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 1.5,
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '100px',
              px: 2.5, py: 1,
              animation: 'fadeSlideUp 0.6s ease 0.2s both',
            }}
          >
            <Schedule sx={{ fontSize: 16, color: GOLD }} />
            <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.85rem', fontWeight: 500 }}>
              {dateStr}
            </Typography>
            <Box sx={{ width: 1, height: 16, bgcolor: 'rgba(255,255,255,0.2)' }} />
            <Typography sx={{ color: '#fff', fontSize: '0.95rem', fontWeight: 700, fontFamily: 'monospace' }}>
              {timeStr}
            </Typography>
          </Box>
        </Container>

        {/* Wave divider */}
        <Box sx={{ position: 'absolute', bottom: -2, left: 0, right: 0, lineHeight: 0 }}>
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" style={{ width: '100%', height: 60 }}>
            <path d="M0,40 C360,80 1080,0 1440,40 L1440,60 L0,60 Z" fill="#F8F4F9" />
          </svg>
        </Box>
      </Box>

      {/* ══════════════════════════════════════════════════
          QUICK STATS ROW
      ══════════════════════════════════════════════════ */}
      <Box sx={{ background: '#F8F4F9', pt: 0, pb: 4 }}>
        <Container maxWidth="lg">
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
            gap: 2,
            mt: -4,
            position: 'relative',
            zIndex: 10,
          }}>
            {quickStats.map((s) => (
              <Box
                key={s.label}
                className="stat-pill"
                sx={{
                  background: '#fff',
                  borderRadius: '16px',
                  px: 2.5, py: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
                  border: `2px solid transparent`,
                  cursor: 'default',
                  '&:hover': {
                    border: `2px solid ${s.color}33`,
                  },
                }}
              >
                <Box sx={{
                  width: 40, height: 40, borderRadius: '10px',
                  background: `${s.color}18`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: s.color,
                  flexShrink: 0,
                }}>
                  {s.icon}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '1.4rem', fontWeight: 800, color: s.color, lineHeight: 1 }}>
                    {s.value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: '#888', fontWeight: 500 }}>
                    {s.label}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ══════════════════════════════════════════════════
          MENU CARDS
      ══════════════════════════════════════════════════ */}
      <Box sx={{ background: '#F8F4F9', pb: 8 }}>
        <Container maxWidth="lg">
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="overline"
              sx={{ color: PINK, fontWeight: 700, letterSpacing: 3, fontSize: '0.7rem' }}
            >
              관리 메뉴
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1A0A2E', mt: 0.5 }}>
              무엇을 관리하시겠습니까?
            </Typography>
          </Box>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr 1fr' },
            gap: 3,
          }}>
            {menuItems.map((item, idx) => (
              <Card
                key={item.path}
                className="menu-card"
                elevation={0}
                sx={{
                  borderRadius: '20px',
                  overflow: 'hidden',
                  border: `1px solid ${item.accentColor}22`,
                  animation: `fadeSlideUp 0.5s ease ${idx * 0.08}s both`,
                  background: '#fff',
                }}
              >
                <CardActionArea
                  onClick={() => navigate(item.path)}
                  sx={{
                    p: 0,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    '&:hover .card-gradient-strip': {
                      opacity: 1,
                    },
                  }}
                >
                  {/* Gradient strip top */}
                  <Box
                    className="card-gradient-strip"
                    sx={{
                      height: 4,
                      background: item.gradient,
                      opacity: 0.7,
                      transition: 'opacity 0.3s ease',
                    }}
                  />

                  <Box sx={{ p: 3, flexGrow: 1 }}>
                    {/* Icon ring */}
                    <Box
                      className="card-icon-ring"
                      sx={{
                        width: 64, height: 64,
                        borderRadius: '18px',
                        background: item.gradient,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff',
                        mb: 2.5,
                        boxShadow: `0 8px 24px ${item.accentColor}44`,
                      }}
                    >
                      {item.icon}
                    </Box>

                    {/* Title */}
                    <Typography
                      sx={{
                        fontWeight: 800,
                        fontSize: '1.1rem',
                        color: '#1A0A2E',
                        mb: 0.5,
                      }}
                    >
                      {item.title}
                    </Typography>

                    {/* Sub-label */}
                    <Typography
                      sx={{
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        color: item.accentColor,
                        textTransform: 'uppercase',
                        letterSpacing: 0.8,
                        mb: 1,
                      }}
                    >
                      {item.sub}
                    </Typography>

                    {/* Description */}
                    <Typography sx={{ fontSize: '0.82rem', color: '#6B7280', lineHeight: 1.5 }}>
                      {item.description}
                    </Typography>
                  </Box>

                  {/* Footer */}
                  <Box sx={{
                    px: 3, py: 1.5,
                    background: `${item.accentColor}08`,
                    borderTop: `1px solid ${item.accentColor}18`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}>
                    <Typography sx={{ fontSize: '0.75rem', color: item.accentColor, fontWeight: 600 }}>
                      관리 페이지로 이동
                    </Typography>
                    <Box sx={{ color: item.accentColor, fontSize: '1.1rem', fontWeight: 700 }}>→</Box>
                  </Box>
                </CardActionArea>
              </Card>
            ))}
          </Box>

          {/* Footer note */}
          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Typography sx={{ fontSize: '0.78rem', color: '#B0A8B9' }}>
              Silver Connect Admin Console · 관리자 전용 시스템 · 무단 접근 금지
            </Typography>
          </Box>
        </Container>
      </Box>
    </AdminLayout>
  );
};

export default AdminMainPage;
